import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Background, Controls, ReactFlow, type NodeTypes, type EdgeTypes } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type {
    ProcessDiagramPositionUpdateType,
    ProcessExecuteIntervalType,
    ProcessIntervalAnchor,
    ProcessTemplateTaskType,
} from '@econtractor/types';
import spinnerTitle from 'components/others/spinnerTitle';
import { getProcessNextTasks, parseProcessTaskInstanceKey, setTaskPosition } from './helpers/nextTask';
import { useProcessDiagram, type ProcessDiagramTaskType } from './model/useProcessDiagram';
import { ProcessDiagramEdge } from './ProcessDiagramEdge';
import { ProcessDiagramNode } from './ProcessDiagramNode';
import ProcessDiagramIntervalDialog from './ProcessDiagramIntervalDialog';

const nodeTypes: NodeTypes = { processTask: ProcessDiagramNode };
const edgeTypes: EdgeTypes = { default: ProcessDiagramEdge };

interface ProcessDiagramProps {
    show: boolean;
    tasks: ProcessDiagramTaskType[];
    readOnly?: boolean;
    onHide: () => void;
    onChange: (tasks: ProcessTemplateTaskType[]) => void;
    onSavePositions?: (positions: ProcessDiagramPositionUpdateType[]) => Promise<boolean>;
}

type IntervalTarget = {
    sourceTemplateId: number;
    sourceDtId: number;
    templateId: number;
    dtId: number;
    answerKey: number | null;
};

const ProcessDiagram = ({ show, tasks, readOnly = false, onHide, onChange, onSavePositions }: ProcessDiagramProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const [intervalTarget, setIntervalTarget] = useState<IntervalTarget | null>(null);
    const [draftTasks, setDraftTasks] = useState(tasks);
    const [layoutDirty, setLayoutDirty] = useState(false);
    const [savingLayout, setSavingLayout] = useState(false);
    const wasOpenRef = useRef(false);
    const layoutOnly = Boolean(readOnly && onSavePositions);

    useEffect(() => {
        if (show && !wasOpenRef.current) {
            setDraftTasks(tasks);
            setLayoutDirty(false);
        }
        wasOpenRef.current = show;
    }, [show, tasks]);

    const diagram = useProcessDiagram({
        tasks: draftTasks,
        readOnly,
        onChange: setDraftTasks,
        onIntervalClick: (sourceTemplateId, sourceDtId, templateId, dtId, answerKey) => {
            if (readOnly) return;
            setIntervalTarget({ sourceTemplateId, sourceDtId, templateId, dtId, answerKey });
        },
    });

    const intervalLink = useMemo(() => {
        if (!intervalTarget) return null;
        const source = draftTasks.find(
            (task) => task.templateId === intervalTarget.sourceTemplateId && task.dtId === intervalTarget.sourceDtId,
        );
        return getProcessNextTasks(source?.processData).find(
            (item) =>
                item.templateId === intervalTarget.templateId &&
                item.dtId === intervalTarget.dtId &&
                item.answerKey === intervalTarget.answerKey,
        ) ?? null;
    }, [draftTasks, intervalTarget]);
    const intervalValue: ProcessExecuteIntervalType | null = intervalLink?.interval ?? null;
    const intervalAnchor: ProcessIntervalAnchor | null = intervalLink?.intervalAnchor ?? null;

    const handleSavePositions = async () => {
        if (!onSavePositions || savingLayout) return;
        const positions = diagram.nodes.flatMap((node) => {
            const inst = parseProcessTaskInstanceKey(node.id);
            if (!inst) return [];
            return [{ templateId: inst.templateId, dtId: inst.dtId, x: node.position.x, y: node.position.y }];
        });
        setSavingLayout(true);
        const saved = await onSavePositions(positions);
        setSavingLayout(false);
        if (!saved) return;
        setLayoutDirty(false);
        setDraftTasks((current) =>
            current.map((task) => {
                const hit = positions.find((item) => item.templateId === task.templateId && item.dtId === task.dtId);
                return hit ? setTaskPosition(task, { x: hit.x, y: hit.y }) : task;
            }),
        );
    };

    return (
        <>
            <Modal show={show} onHide={onHide} fullscreen>
                <Modal.Header closeButton>
                    <Modal.Title>{t('processDiagram.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0 d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-center gap-3 px-3 py-2 border-bottom">
                        <span className="text-muted">
                            {layoutOnly ? t('processDiagram.layoutHint') : t('processDiagram.hint')}
                        </span>
                        {layoutOnly ? (
                            <Button
                                size="sm"
                                variant="primary"
                                className="flex-shrink-0"
                                disabled={!layoutDirty || savingLayout}
                                onClick={() => void handleSavePositions()}
                            >
                                {spinnerTitle(t('common:buttons.save'), savingLayout, t('common:buttons.saving'))}
                            </Button>
                        ) : readOnly ? null : (
                            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="text-muted"
                                    onClick={diagram.handleAutoArrange}
                                >
                                    {t('processDiagram.autoArrange')}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() => {
                                        onChange(draftTasks);
                                        onHide();
                                    }}
                                >
                                    {t('common:buttons.apply')}
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className="process-diagram-canvas flex-grow-1">
                        {draftTasks.length ? (
                            <ReactFlow
                                nodes={diagram.nodes}
                                edges={diagram.edges}
                                nodeTypes={nodeTypes}
                                edgeTypes={edgeTypes}
                                onConnect={diagram.handleConnect}
                                isValidConnection={diagram.isValidConnection}
                                onEdgeClick={diagram.handleEdgeClick}
                                onNodesChange={diagram.handleNodesChange}
                                onNodeDragStart={diagram.handleNodeDragStart}
                                onNodeDragStop={(event, node, nodes) => {
                                    diagram.handleNodeDragStop(event, node, nodes);
                                    if (layoutOnly) setLayoutDirty(true);
                                }}
                                nodesDraggable={layoutOnly || !readOnly}
                                nodesConnectable={!readOnly}
                                edgesFocusable={!readOnly}
                                fitView
                            >
                                <Background />
                                <Controls showInteractive={false} />
                            </ReactFlow>
                        ) : (
                            <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                                {t('processDir.emptyTasks')}
                            </div>
                        )}
                    </div>
                </Modal.Body>
            </Modal>
            <ProcessDiagramIntervalDialog
                show={Boolean(intervalTarget)}
                value={intervalValue}
                anchor={intervalAnchor}
                onHide={() => setIntervalTarget(null)}
                onSave={(interval, anchor) => {
                    if (!intervalTarget) return;
                    diagram.updateLinkInterval(
                        intervalTarget.sourceTemplateId,
                        intervalTarget.sourceDtId,
                        intervalTarget.templateId,
                        intervalTarget.dtId,
                        intervalTarget.answerKey,
                        interval,
                        anchor,
                    );
                    setIntervalTarget(null);
                }}
            />
        </>
    );
};

export default ProcessDiagram;
